import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  Phone,
  User,
  Store,
  Hash,
  Clock3,
  MessageSquare,
  Receipt,
  Image as ImageIcon,
  FileText,
  Video,
  Link as LinkIcon,
  ChevronLeft,
  ChevronsDown,
  RefreshCcw,
  MessageCircle,
  ChevronUp,
  Loader2,
  Send
} from "lucide-react";
import { toast } from "react-hot-toast";
import useOrders from "../Services/OrderServices/useOrders";
import whatsapp from "../../Assets/whatsapp_background.jpg"
import useWhatsapp from "../Services/WhatsappServices/useWhatsapp";
import { decodeEmojiText, twoDecimalPlaces } from "../../Utils/utils";


const CHAT_PAGE_SIZE = 20;

const cleanPhoneNumber = (value = "") => {
  return String(value).replace(/[^\d]/g, "");
};

const normalizeSender = (sender) => String(sender) === "1";

const extractLinks = (text = "") => {
  const regex = /(https?:\/\/[^\s]+)/g;
  return String(text).match(regex) || [];
};

const formatWhatsappMessage = (text = "") => {
  const lines = String(text).split("\n");

  return lines.map((line, index) => {
    const trimmed = line.trim();

    if (!trimmed) {
      return <div key={index} className="h-2" />;
    }

    // Replace **bold** text anywhere in line
    const formattedLine = line.split(/(\*\*.*?\*\*)/g).map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <span key={i} className="font-semibold">
            {part.replace(/\*\*/g, "")}
          </span>
        );
      }

      return <span key={i}>{part}</span>;
    });

    return (
      <div key={index} className="whitespace-pre-wrap break-words">
        {formattedLine}
      </div>
    );
  });
};

const formatDateTime = (value) => {
  if (!value) return "—";
  return value;
};

const getStatusBadgeClass = (status = "") => {
  const s = String(status).toLowerCase();

  if (["delivered", "successful", "completed"].includes(s)) {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  if (["received", "pending", "processing"].includes(s)) {
    return "bg-amber-50 text-amber-700 border-amber-200";
  }
  if (["cancelled", "failed", "rejected"].includes(s)) {
    return "bg-red-50 text-red-700 border-red-200";
  }
  return "bg-slate-50 text-slate-700 border-slate-200";
};

const parseCustomization = (value) => {
  if (!value) return null;

  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

function MediaImage({ src }) {
  return (
    <a href={src} target="_blank" rel="noreferrer" className="block">
      <img
        src={src}
        alt="chat-media"
        className="max-h-64 w-full rounded-xl object-cover border border-slate-200"
      />
    </a>
  );
}

function MediaVideo({ src }) {
  return (
    <video controls className="max-h-72 w-full rounded-xl border border-slate-200">
      <source src={src} />
      Your browser does not support video.
    </video>
  );
}

function DocumentCard({ url }) {
  const name = url?.split("/").pop()?.split("?")[0] || "Document";
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3 hover:bg-slate-50"
    >
      <div className="rounded-lg bg-slate-100 p-2">
        <FileText size={18} className="text-slate-700" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-slate-800">{name}</div>
        <div className="text-xs text-slate-500">Open document</div>
      </div>
    </a>
  );
}

function CatalogueCard({ url }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="block rounded-2xl border border-slate-200 bg-white p-3 hover:bg-slate-50"
    >
      <div className="mb-2 flex items-center gap-2 text-slate-800">
        <Receipt size={16} />
        <span className="font-medium">Catalogue</span>
      </div>
      <div className="text-xs break-all text-blue-600">{url}</div>
    </a>
  );
}

function OrderSummaryText({ text }) {
  const [showOrderLinkModal, setShowOrderLinkModal] = useState(false);
  const links = extractLinks(text);
  const firstLink = links[0];

  return (
    <div className="space-y-2">
      <div className="text-sm text-slate-800">{formatWhatsappMessage(text)}</div>
      {firstLink ? (
        <button
          type="button"
          onClick={() => setShowOrderLinkModal(true)}
          className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 transition-all hover:bg-emerald-100"
        >
          <LinkIcon size={14} />
          Open order details
        </button>
      ) : null}

      {showOrderLinkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">

            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">
                  Order Details
                </h2>

                <p className="text-sm text-slate-500">
                  View order information
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowOrderLinkModal(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-slate-100"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="h-[75vh] bg-slate-100">
              <iframe
                src={firstLink}
                title="Order Details"
                className="h-full w-full border-0"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ReplyPreview({ text }) {
  if (!text) return null;

  return (
    <div className="mb-2 rounded-lg border-l-4 border-emerald-500 bg-white/70 px-3 py-2 text-xs text-slate-700">
      <div className="line-clamp-3 whitespace-pre-wrap break-words">{text}</div>
    </div>
  );
}

function ChatBubble({ item }) {
  const isSent = normalizeSender(item?.sender);
  const type = String(item?.message_type || "").toLowerCase();
  const bubbleBase = isSent
    ? "ml-auto bg-[#d9fdd3] text-black"
    : "mr-auto bg-white text-slblack border border-slate-200";

  const wrapperClass = isSent ? "justify-end" : "justify-start";

  return (
    <div className={`flex ${wrapperClass} mb-3`}>
      <div className="max-w-[85%] sm:max-w-[75%]">
        <div className={`rounded-2xl px-3 py-3 shadow-sm ${bubbleBase}`}>
          <ReplyPreview text={item?.reply_to} />

          {type === "image" ? (
            <div className={isSent ? "text-white" : "text-slate-800"}>
              <MediaImage src={item?.message} />
            </div>
          ) : type === "video" ? (
            <MediaVideo src={item?.message} />
          ) : type === "document" ? (
            <DocumentCard url={item?.message} />
          ) : type === "catalogue" ? (
            <CatalogueCard url={item?.message} />
          ) : type === "button" ? (
            <div
              className={`inline-flex rounded-full px-3 py-1.5 text-sm font-medium ${isSent
                ? "bg-emerald-50 text-black"
                : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                }`}
            >
              {item?.message}
            </div>
          ) : type === "text" ? (
            item?.message?.includes("order details :") ||
              item?.message?.includes(" **Order Summary** ") ? (
              <OrderSummaryText text={item?.message} />
            ) : (
              <div className="text-sm">{formatWhatsappMessage(item?.message)}</div>
            )
          ) : (
            <div className="text-sm whitespace-pre-wrap break-words">
              {typeof item?.message === "string" ? item?.message : JSON.stringify(item?.message)}
            </div>
          )}
        </div>

        <div className={`mt-1 px-1 text-[11px] text-slate-500 ${isSent ? "text-right" : "text-left"}`}>
          {formatDateTime(item?.datetime)}
        </div>
      </div>

    </div>
  );
}

function OrderInfoCard({ order }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="text-lg font-semibold text-slate-900">
            Order #{order?.orderid || "—"}
          </div>
          <div className="mt-1 text-sm text-slate-500">
            {order?.us_name || "Restaurant not found"}
          </div>
        </div>

        <span
          className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium ${getStatusBadgeClass(
            order?.status
          )}`}
        >
          {order?.status || "—"}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-xl bg-slate-50 p-3">
          <div className="text-xs uppercase tracking-wide text-slate-500">Customer</div>
          <div className="mt-1 text-sm font-medium text-slate-900">
            {decodeEmojiText(order?.customer_name) || "—"}
          </div>
          <div className="mt-1 text-sm text-slate-600">
            {order?.customer_mobile || "—"}
          </div>
        </div>

        <div className="rounded-xl bg-slate-50 p-3">
          <div className="text-xs uppercase tracking-wide text-slate-500">Order</div>
          <div className="mt-1 text-sm text-slate-800 capitalize">
            {order?.orderType || "—"}
          </div>
          <div className="mt-1 text-sm text-slate-800">
            Payment: {order?.payment_status || "—"}
          </div>
        </div>

        <div className="rounded-xl bg-slate-50 p-3">
          <div className="text-xs uppercase tracking-wide text-slate-500">Amount</div>
          <div className="mt-1 text-sm text-slate-800">
            Subtotal: ₹{twoDecimalPlaces(order?.subtotal) || "0"}
          </div>
          <div className="mt-1 text-sm text-slate-800">
            Tax: ₹{twoDecimalPlaces(order?.gst) || "0"}
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-900">
            Total: ₹{twoDecimalPlaces(order?.pay_amount) || "0"}
          </div>
        </div>

        <div className="rounded-xl bg-slate-50 p-3">
          <div className="text-xs uppercase tracking-wide text-slate-500">Payment mode</div>
          <div className="mt-1 text-sm text-slate-800">
            {order?.payment_mode || "—"}
          </div>
        </div>

        <div className="rounded-xl bg-slate-50 p-3 sm:col-span-2">
          <div className="text-xs uppercase tracking-wide text-slate-500">Time</div>
          <div className="mt-1 text-sm text-slate-800">
            {order?.order_date || "—"}
          </div>
        </div>
      </div>
    </div>
  );
}


function OrderExtraDetails({ order }) {
  const items = order?.items || [];
  const [expandedItems, setExpandedItems] = useState({}); // { [idx]: true }

  const toggleItem = (idx) => {
    setExpandedItems((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-4 text-sm font-semibold text-slate-800">
        More order details
      </div>

      {/* Meta grid – unchanged */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <DetailCell label="Address" value={order?.address_line} />
        <DetailCell
          label="Delivery partner"
          value={order?.delivery_partner_status}
        />
        <DetailCell label="Table no" value={order?.table_no} />
        <DetailCell label="Refund status" value={order?.refund_status} />
        <DetailCell label="Platform" value={order?.platform} />
        <DetailCell
          label="Virtual account"
          value={order?.virtual_acc}
          breakAll
        />

        <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
          <div className="text-xs uppercase tracking-wide text-slate-500">
            Charges
          </div>
          <div className="mt-1 space-y-0.5">
            <div>
              Packaging: ₹{twoDecimalPlaces(order?.packaging_charges) || "0"}
            </div>
            <div>
              Delivery: ₹{twoDecimalPlaces(order?.delivery_fee) || "0"}
            </div>
            <div>Other: ₹{twoDecimalPlaces(order?.other_fee) || "0"}</div>
          </div>
        </div>

        <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
          <div className="text-xs uppercase tracking-wide text-slate-500">
            Coupon / loyalty
          </div>
          <div className="mt-1 space-y-0.5">
            <div>Coupon: {order?.coupon_code || "—"}</div>
            <div>Points: {order?.loyalty_points || "0"}</div>
          </div>
        </div>
      </div>

      {/* Items – each card is collapsible */}
      {items.length > 0 && (
        <div className="mt-5">
          <div className="mb-3 text-sm font-semibold text-slate-800">
            Items ({items.length})
          </div>

          <div className="space-y-3">
            {items.map((item, idx) => {
              const customization = parseCustomization(item?.customization);
              const isPackage =
                customization?.lineType === "PACKAGE" ||
                customization?.packageType;
              const isExpanded = !!expandedItems[idx];
              const hasDetails =
                customization &&
                (isPackage ||
                  customization.selectedGroups?.length > 0 ||
                  customization.event ||
                  Object.keys(customization).length > 0);

              return (
                <div
                  key={`${item?.item_name || "item"}-${idx}`}
                  className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50"
                >
                  {/* Clickable header */}
                  <button
                    type="button"
                    onClick={() => hasDetails && toggleItem(idx)}
                    className={`flex w-full items-start justify-between gap-3 bg-white px-3 py-3 text-left transition-colors ${
                      hasDetails
                        ? "hover:bg-slate-50 cursor-pointer"
                        : "cursor-default"
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900">
                        {item?.item_name ||
                          customization?.packageName ||
                          item?.product_id ||
                          "Item"}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
                        <span>Qty: {item?.quantity || "0"}</span>
                        <span>•</span>
                        <span>
                          ₹{twoDecimalPlaces(item?.item_price) || "0"}
                        </span>
                        {isPackage && customization?.variantName && (
                          <>
                            <span>•</span>
                            <span className="font-medium text-emerald-700">
                              {customization.variantName}
                              {customization.people
                                ? ` (${customization.people} persons)`
                                : ""}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">
                        ₹{twoDecimalPlaces(item?.total_price) || "0"}
                      </span>
                      {hasDetails && (
                        <ChevronsDown
                          size={16}
                          className={`text-slate-400 transition-transform duration-300 ${
                            isExpanded ? "rotate-180" : ""
                          }`}
                        />
                      )}
                    </div>
                  </button>

                  {/* Collapsible body */}
                  {hasDetails && isExpanded && (
                    <div className="space-y-3 border-t border-slate-200 px-3 py-3">
                      {/* Package chips */}
                      {isPackage && (
                        <div className="flex flex-wrap gap-1.5">
                          {customization.packageType && (
                            <Chip>
                              {String(customization.packageType).replace(
                                /_/g,
                                " "
                              )}
                            </Chip>
                          )}
                          {customization.pricingMethod && (
                            <Chip>
                              {String(customization.pricingMethod).replace(
                                /_/g,
                                " "
                              )}
                            </Chip>
                          )}
                          {customization.basePrice != null && (
                            <Chip>
                              Base ₹{twoDecimalPlaces(customization.basePrice)}
                            </Chip>
                          )}
                          {customization.extrasPrice > 0 && (
                            <Chip>
                              Extras ₹
                              {twoDecimalPlaces(customization.extrasPrice)}
                            </Chip>
                          )}
                        </div>
                      )}

                      {/* Selected groups */}
                      {Array.isArray(customization.selectedGroups) &&
                        customization.selectedGroups.length > 0 && (
                          <div className="space-y-2.5">
                            {customization.selectedGroups.map((group) => (
                              <div
                                key={
                                  group.packageOptionGroupId ||
                                  group.optionGroupId ||
                                  group.groupTitle
                                }
                                className="rounded-lg border border-slate-200 bg-white p-2.5"
                              >
                                <div className="mb-1.5 flex items-center justify-between gap-2">
                                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                    {group.groupTitle || "Options"}
                                  </span>
                                  {group.quantityRule && (
                                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">
                                      {String(group.quantityRule).replace(
                                        /_/g,
                                        " "
                                      )}
                                    </span>
                                  )}
                                </div>

                                <ul className="space-y-1">
                                  {(group.items || []).map((opt, optIdx) => (
                                    <li
                                      key={
                                        opt.itemId ||
                                        opt.optionGroupItemId ||
                                        optIdx
                                      }
                                      className="flex items-start justify-between gap-2 text-sm text-slate-800"
                                    >
                                      <span className="min-w-0">
                                        {opt.itemName || "Item"}
                                        {opt.variantName ? (
                                          <span className="text-slate-500">
                                            {" "}
                                            · {opt.variantName}
                                          </span>
                                        ) : null}
                                        {opt.quantityPerPerson != null && (
                                          <span className="ml-1 text-xs text-slate-500">
                                            (×{opt.quantityPerPerson}/person)
                                          </span>
                                        )}
                                      </span>
                                      {opt.extraPrice > 0 && (
                                        <span className="shrink-0 text-xs font-medium text-emerald-700">
                                          +₹{twoDecimalPlaces(opt.extraPrice)}
                                        </span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        )}

                      {/* Event */}
                      {customization.event && (
                        <div className="rounded-lg border border-slate-200 bg-white p-2.5">
                          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
                            Event
                          </div>
                          <div className="grid grid-cols-1 gap-1 text-sm text-slate-800 sm:grid-cols-2">
                            {customization.event.location && (
                              <div>
                                <span className="text-slate-500">
                                  Location:{" "}
                                </span>
                                {customization.event.location}
                              </div>
                            )}
                            {customization.event.eventDate && (
                              <div>
                                <span className="text-slate-500">Date: </span>
                                {customization.event.eventDate}
                              </div>
                            )}
                            {(customization.event.startTime ||
                              customization.event.endTime) && (
                              <div className="sm:col-span-2">
                                <span className="text-slate-500">Time: </span>
                                {customization.event.startTime || "—"}
                                {customization.event.endTime
                                  ? ` – ${customization.event.endTime}`
                                  : ""}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Simple key-value fallback */}
                      {!isPackage &&
                        !customization.selectedGroups &&
                        typeof customization === "object" && (
                          <div className="flex flex-wrap gap-1.5">
                            {Object.entries(customization).map(
                              ([key, value]) => {
                                if (
                                  value == null ||
                                  typeof value === "object"
                                )
                                  return null;
                                return (
                                  <Chip key={key}>
                                    {key}: {String(value)}
                                  </Chip>
                                );
                              }
                            )}
                          </div>
                        )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function DetailCell({ label, value, breakAll = false }) {
  return (
    <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
      <div className="text-xs uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className={`mt-1 ${breakAll ? "break-all" : ""}`}>
        {value || "—"}
      </div>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-xs font-medium text-slate-700">
      {children}
    </span>
  );
}

export default function OrderChatDrawer({
  open,
  onClose,
  orderId,

}) {
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderError, setOrderError] = useState("");
  const [orderDetails, setOrderDetails] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundRemark, setRefundRemark] = useState("");
  const [refundType, setRefundType] = useState("");
  const [statusValue, setStatusValue] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState("");
  const [messages, setMessages] = useState([]);
  const [chatPage, setChatPage] = useState(1);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [refreshingChat, setRefreshingChat] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const chatContainerRef = useRef(null);
  const initialScrollDone = useRef(false);
  const previousScrollHeight = useRef(0);
  const fetchingMoreRef = useRef(false);
  const {
    getOrderDetails,
    updateOrderPaymentStatus,
    requestCustomerAddress,
    assignDeliveryPartner,
    processRefund,
    refundV1,
    generatePaymentIntent,
    generatePaymentIntent1,
    checkEasebuzzTransaction,
    changeCatalogueOrderStatus,
  } = useOrders();

  const { getWhatsappConversation, sendInstantMessage } = useWhatsapp();

  const normalizedContact = useMemo(
    () => cleanPhoneNumber(orderDetails?.customer_mobile),
    [orderDetails?.customer_mobile]
  );

  const fetchOrderData = useCallback(async () => {
    if (!open || !orderId) return;

    setOrderLoading(true);
    setOrderError("");

    try {
      const result = await getOrderDetails(orderId);

      if (result && Object.keys(result).length > 0) {
        setOrderDetails(result);
      } else {
        setOrderDetails(null);
        setOrderError("No order details found");
      }
    } catch (e) {
      setOrderError("Unable to load order details");
    } finally {
      setOrderLoading(false);
    }
  }, [open, orderId, getOrderDetails]);

  const fetchConversationData = useCallback(
    async ({ page = 1, reset = false } = {}) => {
      if (!open || !orderDetails?.fk_user_id || !normalizedContact) return;
      if (fetchingMoreRef.current) return;
      if (!reset && !hasMoreChats) return;

      fetchingMoreRef.current = true;
      setChatLoading(true);
      setChatError("");

      try {
        const container = chatContainerRef.current;
        if (!reset && container) {
          previousScrollHeight.current = container.scrollHeight;
        }

        const result = await getWhatsappConversation({
          userid: orderDetails?.fk_user_id,
          contact: normalizedContact,
          pageIndex: page,
          recordPerPage: 20,
          secret: "",
        });

        if (!result?.success) {
          throw new Error(result?.message || "Failed to fetch conversation");
        }

        const newMessages = Array.isArray(result?.data) ? result.data : [];
        const reversed = [...newMessages].reverse();

        if (reset) {
          setMessages(reversed);
          setChatPage(2);
          setHasMoreChats(newMessages.length === 20);

          requestAnimationFrame(() => {
            const el = chatContainerRef.current;
            if (el && !initialScrollDone.current) {
              el.scrollTop = el.scrollHeight;
              initialScrollDone.current = true;
            }
          });
        } else {
          setMessages((prev) => [...reversed, ...prev]);
          setChatPage((prev) => prev + 1);
          setHasMoreChats(newMessages.length === 20);

          requestAnimationFrame(() => {
            const el = chatContainerRef.current;
            if (el) {
              const newHeight = el.scrollHeight;
              const diff = newHeight - previousScrollHeight.current;
              el.scrollTop = diff;
            }
          });
        }
      } catch (error) {
        setChatError(error?.message || "Unable to load chat");
      } finally {
        fetchingMoreRef.current = false;
        setChatLoading(false);
      }
    },
    [open, orderDetails?.fk_user_id, normalizedContact, hasMoreChats, getWhatsappConversation]
  );

  useEffect(() => {
    if (!open || !orderId) return;

    setMessages([]);
    setChatPage(1);
    setHasMoreChats(true);
    setChatError("");
    initialScrollDone.current = false;
    previousScrollHeight.current = 0;

    fetchOrderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId]);

  useEffect(() => {
    if (!open || !orderDetails?.fk_user_id || !normalizedContact) return;

    setMessages([]);
    setChatPage(1);
    setHasMoreChats(true);
    setChatError("");
    initialScrollDone.current = false;
    previousScrollHeight.current = 0;

    fetchConversationData({ page: 1, reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderDetails?.fk_user_id, normalizedContact]);

  useEffect(() => {
    if (!open) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);
  useEffect(() => {
    if (orderDetails?.status) {
      setStatusValue(orderDetails.status);
    } else {
      setStatusValue("");
    }
  }, [orderDetails]);
  const handleUpdatePaymentStatus = async () => {
    if (!orderDetails?.orderid) return;

    setActionBusy(true);
    const result = await updateOrderPaymentStatus(orderDetails.orderid);

    if (result?.success) {
      toast.success(result?.message || "Payment updated");
      await fetchOrderData();
    } else {
      toast.error(result?.message || "Failed to update payment");
    }
    setActionBusy(false);
  };
  const handleRequestAddress = async () => {
    if (!orderDetails?.orderid) return;

    setActionBusy(true);
    const result = await requestCustomerAddress(orderDetails.orderid);

    if (result?.success) {
      toast.success(result?.message || "Address request sent");
    } else {
      toast.error(result?.message || "Failed to send request");
    }
    setActionBusy(false);
  };
  const handleGeneratePaymentLink = async () => {
    if (!orderDetails?.orderid) return;

    setActionBusy(true);

    const result = await generatePaymentIntent({
      userid: orderDetails?.fk_user_id || "",
      orderId: orderDetails?.orderid,
      custName: orderDetails?.customer_name || "",
      custMobile: orderDetails?.customer_mobile || "",
    });

    if (result?.success) {
      toast.success(result?.message || "Payment link sent");
    } else {
      toast.error(result?.message || "Failed to send payment link");
    }

    setActionBusy(false);
  };

    const handleGeneratePaymentLink1 = async () => {
    if (!orderDetails?.orderid) return;

    setActionBusy(true);

    const result = await generatePaymentIntent1({
      userid: orderDetails?.fk_user_id || "",
      orderId: orderDetails?.orderid,
      custName: orderDetails?.customer_name || "",
      custMobile: orderDetails?.customer_mobile || "",
    });

    if (result?.success) {
      toast.success(result?.message || "Payment link sent");
    } else {
      toast.error(result?.message || "Failed to send payment link");
    }

    setActionBusy(false);
  };
  const handleAssignDeliveryPartner = async () => {
    if (!orderDetails?.orderid) return;

    setActionBusy(true);

    const result = await assignDeliveryPartner({
      userid: orderDetails?.fk_user_id || "",
      orderId: orderDetails?.orderid,
      paymentMode:
        String(orderDetails?.payment_status || "").toLowerCase() === "unpaid"
          ? "COD"
          : null,
    });

    if (result?.success) {
      toast.success(result?.message || "Sent to delivery partner");
      await fetchOrderData();
    } else {
      toast.error(result?.message || "Failed to assign delivery partner");
    }

    setActionBusy(false);
  };
  const handleChangeOrderStatus = async () => {
    if (!orderDetails?.orderid || !statusValue) return;

    setActionBusy(true);

    const result = await changeCatalogueOrderStatus({
      clientUserId: orderDetails?.fk_user_id || "",
      orderid: orderDetails?.orderid,
      chosenStatus: statusValue,
    });

    if (result?.success) {
      toast.success(result?.message || "Order status updated");
      await fetchOrderData();
    } else {
      toast.error(result?.message || "Failed to update order status");
    }

    setActionBusy(false);
  };
  const handleRefund = async () => {
    if (!orderDetails?.fk_user_id || !orderDetails?.virtual_acc) {
      toast.error("Missing refund details");
      return;
    }

    setActionBusy(true);

    const result = await processRefund({
      userid: orderDetails?.fk_user_id,
      amount: Math.trunc(Number(orderDetails?.pay_amount || 0)).toString(),
      transactionId: String(orderDetails?.virtual_acc || ""),
      remark: `Refund for order ${orderDetails?.orderid}`,
    });

    if (result?.success) {
      toast.success(result?.message || "Refund initiated");
      await fetchOrderData();
    } else {
      toast.error(result?.message || "Refund failed");
    }

    setActionBusy(false);
  };
  const handleEasebuzzRefund = () => {
    if (!orderDetails?.orderid) {
      toast.error("Missing order id");
      return;
    }

    const maxAmount = Number(orderDetails?.pay_amount || 0);
    if (!Number.isFinite(maxAmount) || maxAmount <= 0) {
      toast.error("Invalid refundable amount");
      return;
    }

    setRefundAmount(maxAmount.toFixed(2));
    setRefundRemark("");
    setRefundType("");
    setRefundModalOpen(true);
  };

  const handleConfirmRefund = async () => {
    const maxAmount = Number(orderDetails?.pay_amount || 0);
    const amount = Number(refundAmount);
    const remark = refundRemark.trim();

    if (!refundType) return toast.error("Please select a refund type");
    if (!remark) return toast.error("Please enter a remark");
    if (!Number.isFinite(amount) || amount <= 0) {
      return toast.error("Please enter a valid refund amount");
    }
    if (amount > maxAmount) {
      return toast.error(`Refund amount cannot exceed ₹${maxAmount.toFixed(2)}`);
    }

    setActionBusy(true);
    const result = await refundV1({
      orderId: orderDetails.orderid,
      amount,
      remark,
      type: refundType,
    });

    if (result?.success) {
      toast.success(result?.message || "Refund initiated");
      setRefundModalOpen(false);
      await fetchOrderData();
    } else {
      toast.error(result?.message || "Refund failed");
    }

    setActionBusy(false);
  };
  const handleCheckEasebuzz = async () => {
    const txnId =
      orderDetails?.txnID ||
      orderDetails?.transaction_id ||
      orderDetails?.virtual_acc;

    if (!txnId) {
      toast.error("No transaction id found");
      return;
    }

    setActionBusy(true);

    const result = await checkEasebuzzTransaction(txnId);

    if (result?.success) {
      toast.success(result?.message || "Status fetched");
      console.log("Easebuzz status:", result?.data);
    } else {
      toast.error(result?.message || "Failed to check status");
    }

    setActionBusy(false);
  };
  const handleRefreshConversation = async () => {
    if (!orderDetails?.fk_user_id || !normalizedContact) return;

    setRefreshingChat(true);
    setMessages([]);
    setChatPage(1);
    setHasMoreChats(true);
    setChatError("");
    initialScrollDone.current = false;
    previousScrollHeight.current = 0;

    await fetchConversationData({ page: 1, reset: true });
    setRefreshingChat(false);
  };

  const handleSendTextMessage = async () => {
    const trimmed = messageText.trim();

    if (!trimmed) return;
    if (!orderDetails?.fk_user_id || !normalizedContact) return;

    setSendingMessage(true);

    const result = await sendInstantMessage({
      userid: orderDetails?.fk_user_id,
      contact: normalizedContact,
      message: trimmed,
      secret: "NA",
    });

    if (result?.success) {
      toast.success(result?.message || "Message sent");
      setMessageText("");

      setMessages([]);
      setChatPage(1);
      setHasMoreChats(true);
      setChatError("");
      initialScrollDone.current = false;
      previousScrollHeight.current = 0;

      await fetchConversationData({ page: 1, reset: true });
    } else {
      toast.error(result?.message || "Failed to send message");
    }

    setSendingMessage(false);
  };
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full max-w-[980px] bg-slate-100 shadow-2xl">
        <div className="flex h-full flex-col">
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-slate-900">
                <MessageSquare size={18} />
                <h2 className="truncate text-lg font-semibold">Order chat & details</h2>
              </div>
              <div className="mt-1 text-sm text-slate-500">Slide panel for order info and WhatsApp conversation</div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            >
              <X size={18} />
            </button>
          </div>

          <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[360px_minmax(0,1fr)]">
            <div className="overflow-y-auto border-b border-slate-200 bg-slate-50 p-4 lg:border-b-0 lg:border-r">
              {orderLoading ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
                  Loading order details...
                </div>
              ) : orderError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  {orderError}
                </div>
              ) : orderDetails ? (
                <div className="space-y-4">
                  <OrderInfoCard order={orderDetails} />

                  <button
                    type="button"
                    onClick={() => setShowMoreDetails((prev) => !prev)}
                    className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <span>
                      {showMoreDetails
                        ? "Hide more details"
                        : "View more details"}
                    </span>

                    <ChevronsDown
                      className={`transition-transform duration-300 ${showMoreDetails ? "rotate-180" : ""
                        }`}
                      size={18}
                    />
                  </button>

                  {showMoreDetails && (
                    <div className="mt-4">
                      <OrderExtraDetails order={orderDetails} />
                    </div>
                  )}

                  <div className="space-y-3">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="mb-3 text-sm font-semibold text-slate-800">Status update</div>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
                        <select
                          className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none transition-all focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
                          value={statusValue}
                          onChange={(e) => setStatusValue(e.target.value)}
                        >
                          <option value="">Select status</option>

                          <option value="received">Received</option>
                          <option value="paid">Paid</option>
                          <option value="accepted">Accepted</option>
                          <option value="prepared">Prepared</option>
                          <option value="rejected">Rejected</option>
                          <option value="refunded">Refunded</option>

                          <option value="driver_assigned">Driver Assigned</option>
                          <option value="driver_confirmed">Driver Confirmed</option>
                          <option value="driver_pickup_arrival">
                            Driver Pickup Arrival
                          </option>

                          <option value="delivery_picked">Delivery Picked</option>

                          <option value="driver_dropoff_arrival">
                            Driver Dropoff Arrival
                          </option>

                          <option value="completed">Completed</option>
                          <option value="dispatched">Dispatched</option>
                          <option value="delivered">Delivered</option>

                          <option value="ready_for_dinning">
                            Ready For Dinning
                          </option>

                          <option value="ready_for_takeway">
                            Ready For Takeway
                          </option>

                          <option value="not-delivered">Not Delivered</option>
                        </select>

                        <button
                          className="h-11 rounded-xl bg-emerald-600 px-4 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                          onClick={handleChangeOrderStatus}
                          disabled={!statusValue || actionBusy}
                        >
                          {actionBusy ? "Updating..." : "Update"}
                        </button>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="mb-3 text-sm font-semibold text-slate-800">Order actions</div>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <button
                          className="w-full rounded-xl bg-emerald-600 px-3 py-2.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                          onClick={handleUpdatePaymentStatus}
                          disabled={!orderDetails?.orderid || actionBusy}
                        >
                          Update Payment
                        </button>

                        {/* <button
                          className="w-full rounded-xl bg-blue-600 px-3 py-2.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                          onClick={handleGeneratePaymentLink}
                          disabled={!orderDetails?.orderid || actionBusy}
                        >
                          Send Payment L1
                        </button> */}
                         {/* <button
                          className="w-full rounded-xl bg-blue-600 px-3 py-2.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                          onClick={handleGeneratePaymentLink1}
                          disabled={!orderDetails?.orderid || actionBusy}
                        >
                          Send Payment L2
                        </button> */}

                        <button
                          className="w-full rounded-xl bg-violet-600 px-3 py-2.5 text-xs font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
                          onClick={handleAssignDeliveryPartner}
                          disabled={!orderDetails?.orderid || actionBusy}
                        >
                          Send To Packky
                        </button>

                        <button
                          className="w-full rounded-xl bg-cyan-600 px-3 py-2.5 text-xs font-semibold text-white hover:bg-cyan-700 disabled:opacity-60"
                          onClick={handleRequestAddress}
                          disabled={!orderDetails?.orderid || actionBusy}
                        >
                          Request Address
                        </button>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="mb-3 text-sm font-semibold text-slate-800">Payment tools</div>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <button
                          className="w-full rounded-xl bg-rose-600 px-3 py-2.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
                          onClick={handleRefund}
                          disabled={!orderDetails?.orderid || actionBusy}
                        >
                          Refund Pay
                        </button>

                        <button
                          className="w-full rounded-xl bg-indigo-600 px-3 py-2.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                          onClick={handleEasebuzzRefund}
                          disabled={!orderDetails?.orderid || actionBusy}
                        >
                          Refund V1
                        </button>

                        <button
                          className="w-full rounded-xl bg-amber-600 px-3 py-2.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-60 sm:col-span-2"
                          onClick={handleCheckEasebuzz}
                          disabled={actionBusy}
                        >
                          Check Payment
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
                  No order details found.
                </div>
              )}
            </div>

            <div className="flex h-full min-h-0 flex-col overflow-hidden border border-slate-200 bg-white shadow-sm">
              {/* Header */}
              <div className="border-b border-slate-200 bg-gradient-to-r from-white to-slate-50 px-4 py-3">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm">
                      <User size={14} className="text-emerald-600" />
                      <span className="max-w-[140px] truncate">
                        {decodeEmojiText(orderDetails?.customer_name) || "Customer"}
                      </span>
                    </div>

                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm">
                      <Phone size={14} className="text-blue-600" />
                      <span>{normalizedContact || "No number"}</span>
                    </div>

                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm">
                      <Store size={14} className="text-orange-500" />
                      <span className="max-w-[140px] truncate">
                        {orderDetails?.us_name || "Restaurant"}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleRefreshConversation}
                    disabled={chatLoading || refreshingChat}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-all hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <RefreshCcw
                      size={15}
                      className={refreshingChat ? "animate-spin" : ""}
                    />
                    {refreshingChat ? "Refreshing..." : "Refresh"}
                  </button>
                </div>
              </div>

              {/* Chat Area */}
              <div
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto px-4 py-5 "
                style={{
                  backgroundImage: `url(${whatsapp})`,
                  backgroundBlendMode: "overlay",
                }}
                onScroll={(e) => {
                  const el = e.currentTarget;

                  if (el.scrollTop < 80 && hasMoreChats && !chatLoading) {
                    fetchConversationData({ page: chatPage, reset: false });
                  }
                }}
              >
                {/* Initial Loader */}
                {chatLoading && messages.length === 0 ? (
                  <div className="flex justify-center py-10">
                    <div className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-500 shadow-sm">
                      Loading conversation...
                    </div>
                  </div>
                ) : null}

                {/* Older Messages Loader */}
                {chatLoading && messages.length > 0 ? (
                  <div className="mb-4 flex justify-center">
                    <div className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-medium text-slate-500 shadow-sm">
                      Loading older messages...
                    </div>
                  </div>
                ) : null}

                {/* Error */}
                {chatError ? (
                  <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 shadow-sm">
                    {chatError}
                  </div>
                ) : null}

                {/* Empty State */}
                {!chatLoading && messages.length === 0 && !chatError ? (
                  <div className="flex h-full items-center justify-center">
                    <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-8 py-10 text-center shadow-sm">
                      <MessageCircle className="mx-auto mb-3 text-slate-400" size={34} />

                      <p className="text-sm font-medium text-slate-600">
                        No conversation found
                      </p>

                      <p className="mt-1 text-xs text-slate-400">
                        Messages will appear here
                      </p>
                    </div>
                  </div>
                ) : null}

                {/* Scroll Hint */}
                {hasMoreChats && messages.length > 0 ? (
                  <div className="mb-5 flex justify-center">
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-medium text-slate-500 shadow-sm">
                      <ChevronUp size={13} />
                      Scroll up to load older messages
                    </div>
                  </div>
                ) : null}

                {/* Messages */}
                <div className="space-y-3">
                  {messages.map((item) => (
                    <ChatBubble
                      key={`${item?.message_id}-${item?.datetime}`}
                      item={item}
                    />
                  ))}
                </div>
              </div>

              {/* Input Area */}
              <div className="border-t border-slate-200 bg-white p-3">
                <div className="flex items-end gap-3 rounded-3xl border border-slate-200 bg-slate-50 p-2 shadow-sm">
                  <textarea
                    rows={1}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Type your message..."
                    className="max-h-36 min-h-[48px] flex-1 resize-none bg-transparent px-3 py-3 text-sm text-slate-700 outline-none placeholder:text-slate-400"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendTextMessage();
                      }
                    }}
                  />

                  <button
                    type="button"
                    onClick={handleSendTextMessage}
                    disabled={sendingMessage || !messageText.trim()}
                    className="inline-flex h-12 items-center justify-center rounded-2xl bg-emerald-800 px-5 text-sm font-semibold text-white transition-all hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {sendingMessage ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <>
                        <Send size={16} className="mr-2" />
                        Send
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>      {refundModalOpen ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={() => !actionBusy && setRefundModalOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div>
                <h3 className="text-base font-bold text-slate-900">Confirm Refund</h3>
                <p className="mt-1 text-xs text-slate-500">Order #{orderDetails?.orderid}</p>
              </div>
              <button
                type="button"
                onClick={() => setRefundModalOpen(false)}
                disabled={actionBusy}
                className="rounded-full p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-700">
                Maximum refundable amount: <b>₹{Number(orderDetails?.pay_amount || 0).toFixed(2)}</b>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-700">Refund Type *</label>
                <select
                  value={refundType}
                  onChange={(e) => setRefundType(e.target.value)}
                  disabled={actionBusy}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-200"
                >
                  <option value="">Select refund type</option>
                  <option value="YESBANKUPI">YESBANKUPI</option>
                  <option value="EASEBUZZ">EASEBUZZ</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-700">Refund Amount *</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  disabled={actionBusy}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-700">Remark *</label>
                <textarea
                  rows={3}
                  value={refundRemark}
                  onChange={(e) => setRefundRemark(e.target.value)}
                  disabled={actionBusy}
                  placeholder="Enter refund remark"
                  className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t px-5 py-4">
              <button
                type="button"
                onClick={() => setRefundModalOpen(false)}
                disabled={actionBusy}
                className="rounded-xl border px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRefund}
                disabled={
                  actionBusy ||
                  !refundType ||
                  !refundRemark.trim() ||
                  !refundAmount ||
                  Number(refundAmount) <= 0 ||
                  Number(refundAmount) > Number(orderDetails?.pay_amount || 0)
                }
                className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {actionBusy ? <Loader2 size={16} className="animate-spin" /> : null}
                Confirm Refund
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}